/**
 * Home Page
 * 
 * Project: SunMarbella Holiday Rental Website
 * Version: 1.0.0
 * Last Modified: 2025-03-09
 * 
 * Purpose:
 * Main landing page with hero section, search functionality, featured properties, and trust indicators.
 * Optimized for conversion with clear CTAs and property showcase.
 * 
 * Features:
 * - Hero section with background image and search bar
 * - Featured properties grid (4-6 properties)
 * - Trust badges and testimonials
 * - Why SunMarbella section (benefits)
 * - Newsletter signup
 * - Responsive design (mobile-first)
 * - Analytics tracking for user interactions
 * 
 * Components:
 * - Hero (main hero section with search)
 * - SearchBar (property search interface)
 * - PropertyCard (featured property listings - 6 properties)
 * - TrustBar (testimonials and badges)
 * - Footer (site footer)
 * 
 * Dependencies:
 * - React 19.2.4 (JSX)
 * - React Router 7 (navigation)
 * - TailwindCSS 4.1 (responsive styling)
 * - Framer Motion 12 (animations)
 * - SearchBarContext (search state)
 * 
 * State Management:
 * - Uses SearchBarContext for search parameters
 * - Featured properties from mock data
 * 
 * SEO Optimization:
 * - Meta tags for homepage
 * - Structured data for organization
 * - Open Graph tags for social sharing
 * 
 * Responsive Breakpoints:
 * - Mobile: Single column layout
 * - Tablet (md): 2 column layout
 * - Desktop (lg): 3-4 column layout
 * 
 * CHANGELOG:
 * - 1.0.0 (2025-03-09): Initial implementation with hero and featured properties
 * - 2026-03-05: Standardized container width to max-w-7xl (was max-w-6xl)
 * - 2026-03-05: Standardized CTA button to match site-wide style (uppercase, tracking, font-black)
 * - 2026-03-05: Wired "Browse Our Vetted Villas" CTA to /destinations with analytics
 * - Previous: Inconsistent CTA styling, narrower container than rest of site
 */
import React, { useEffect, useRef, useState } from 'react';
import { Hero } from '../components/Hero';
import { TrustBar } from '../components/TrustBar';
import { PropertyCard } from '../components/PropertyCard';
import { NewsletterSignup } from '../components/NewsletterSignup';
import { useNavigate } from 'react-router-dom';
import { trackEvent, trackViewItemList } from '../lib/analytics';
import { ProIcon } from '../components/ProIcon';

// Mock data for local design testing
import { MOCK_PROPERTIES, toPropertyCardFormat, USE_MOCK_DATA } from '../mock/index';

const getSourceIcon = (source: string) => {
  if (source === 'Google Review') return <ProIcon icon="google" family="brands" className="w-4 h-4" />;
  if (source === 'Trustpilot') return <ProIcon icon="star" family="solid" className="w-4 h-4 text-brand-sun" />;
  return <ProIcon icon="bullseye-arrow" family="light" className="w-4 h-4 text-white" />;
};

export const Home = () => {
  const navigate = useNavigate();
  const [featuredProperties, setFeaturedProperties] = useState<any[]>([]);
  const mobileReviewsRef = useRef<HTMLDivElement>(null);
  const hostTestimonialsRef = useRef<HTMLDivElement>(null);

  const hostTestimonials = [
    {
      quote: "Having someone local who actually knows each rental made all the difference. They helped us choose the perfect spot for our family and were there when we needed them.",
      byline: "The Johnson Family, UK",
      season: "SUMMER STAY",
      source: "Direct booking",
      rating: 5.0,
    },
    {
      quote: "Dragos spent 45 minutes on a video call showing us around three rentals. He knew every detail—from pool depth to the nearest pharmacy. That's the personal touch we needed.",
      byline: "The Williams Family, London",
      season: "JULY STAY",
      source: "Direct booking",
      rating: 5.0,
    },
    {
      quote: "When our flight was delayed until 2am, someone was waiting at the rental with keys. No extra charge, no complaints. Just genuine care for families.",
      byline: "The Martinez Family, Barcelona",
      season: "AUGUST STAY",
      source: "Direct booking",
      rating: 5.0,
    },
  ];

  const slideHostTestimonials = (direction: 'left' | 'right') => {
    const container = hostTestimonialsRef.current;
    if (!container) return;
    const firstCard = container.querySelector<HTMLElement>('[data-host-testimonial-card]');
    const delta = firstCard ? firstCard.offsetWidth + 16 : container.clientWidth;
    container.scrollBy({
      left: direction === 'right' ? delta : -delta,
      behavior: 'smooth',
    });
  };
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  
  // Touch swipe state for desktop testimonial
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchEndX, setTouchEndX] = useState<number | null>(null);
  const minSwipeDistance = 50;

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEndX(null);
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStartX || !touchEndX) return;
    const distance = touchStartX - touchEndX;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      setCurrentTestimonial((prev) => (prev + 1) % hostTestimonials.length);
    } else if (isRightSwipe) {
      setCurrentTestimonial((prev) => (prev - 1 + hostTestimonials.length) % hostTestimonials.length);
    }
    setTouchStartX(null);
    setTouchEndX(null);
  };

  const handleCardClick = () => {
    // Only advance on click if not a touch swipe
    if (!touchStartX && !touchEndX) {
      setCurrentTestimonial((prev) => (prev + 1) % hostTestimonials.length);
    }
  };

  const reviews = [
    {
      quote: 'From the airport transfer to checkout, everything felt effortless. The rental was exactly as described, and the team sorted a small issue with the pool heater within two hours.',
      byline: 'The Jensen Family, Copenhagen',
      season: 'SUMMER STAY',
      source: 'Direct booking',
      rating: 5.0,
    },
    {
      quote: "We've rented through Airbnb before and this was on a completely different level. The rental had been properly checked, everything worked, and someone actually answered when we called.",
      byline: 'The Muller Family, Munich',
      season: 'EASTER STAY',
      source: 'Google Review',
      rating: 5.0,
    },
    {
      quote: "Booking direct saved us over EUR400 vs. what we'd seen on the platforms. The concierge pre-stocked the fridge and had a cot ready for our youngest. Absolute game changer.",
      byline: 'The Harrison Family, Manchester',
      season: 'JULY STAY',
      source: 'Direct booking',
      rating: 5.0,
    },
    {
      quote: "Third year booking with SUNMARBELLA. The rentals are always exactly what they say they are. We've recommended them to six families now.",
      byline: 'The Andersen Family, Stockholm',
      season: 'AUGUST STAY',
      source: 'Trustpilot',
      rating: 5.0,
    },
    {
      quote: "They answered a WhatsApp at 11pm when we couldn't find the key safe code. Immediately helpful, no attitude. That's the kind of service that makes you come back.",
      byline: 'The Van Der Berg Family, Amsterdam',
      season: 'JUNE STAY',
      source: 'Direct booking',
      rating: 5.0,
    },
    {
      quote: "Our daughter has mobility needs and the team went out of their way to check the rental in advance and send us a video walkthrough. Exceptional care.",
      byline: "The O'Brien Family, Dublin",
      season: 'SEPTEMBER STAY',
      source: 'Google Review',
      rating: 5.0,
    },
    {
      quote: "The pool was spotless, beds were comfortable, and the kitchen had everything we needed. Small things matter on family holidays and they got every detail right.",
      byline: 'The Schmidt Family, Berlin',
      season: 'MAY STAY',
      source: 'Trustpilot',
      rating: 5.0,
    },
    {
      quote: "Booked last minute for school half-term. Found us a rental in 24 hours that other agencies said was impossible. The local knowledge is unmatched.",
      byline: 'The Thompson Family, London',
      season: 'OCTOBER STAY',
      source: 'Direct booking',
      rating: 5.0,
    },
    {
      quote: "After a bad experience with a rental that looked nothing like photos elsewhere, SUNMARBELLA's honest descriptions and personal verification sold us. Reality exceeded expectations.",
      byline: 'The Dubois Family, Paris',
      season: 'APRIL STAY',
      source: 'Google Review',
      rating: 5.0,
    },
  ];

  const slideReviews = (direction: 'left' | 'right') => {
    const container = mobileReviewsRef.current;
    if (!container) return;
    const firstCard = container.querySelector<HTMLElement>('[data-review-card]');
    const delta = firstCard ? firstCard.offsetWidth + 16 : container.clientWidth;
    container.scrollBy({
      left: direction === 'right' ? delta : -delta,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    if (USE_MOCK_DATA) {
      // Use mock data for design testing - take first 6 properties
      const mockFormatted = MOCK_PROPERTIES.slice(0, 6).map(toPropertyCardFormat);
      setFeaturedProperties(mockFormatted);
    } else {
      // Use real API when ready
      fetch('/api/properties')
        .then(res => res.json())
        .then((data: any[]) => setFeaturedProperties(data.slice(0, 6)));
    }
  }, []);

  useEffect(() => {
    if (featuredProperties.length === 0) return;
    trackViewItemList(
      'homepage_featured_rentals',
      featuredProperties.map((property: any) => ({
        id: property.id,
        name: property.title,
        price: property.price,
        category: property.badges?.[0],
      }))
    );
  }, [featuredProperties]);

  return (
    <main className="overflow-x-hidden">
      <Hero />
      <TrustBar />

      <section className="py-10 sm:py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8 md:gap-16 items-start">
            {/* Left Column - Local Host Profile */}
            <div className="space-y-5 sm:space-y-8 min-w-0">
              {/* Large Photo Centerpiece */}
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-5 sm:mb-6">
                  {/* Large Circular Photo - Responsive sizing */}
                  <div className="w-28 h-28 sm:w-40 sm:h-40 md:w-48 md:h-48 rounded-full ring-4 ring-brand-ocean/20 shadow-2xl overflow-hidden bg-slate-100">
                    <img 
                      src="/assets/host-dragos.webp" 
                      alt="Dragos Moscu - SUNMARBELLA Founder and Local Expert" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {/* Overlapping Badge */}
                  <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-brand-ocean text-white px-3 sm:px-5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold tracking-wide shadow-lg whitespace-nowrap text-center">
                    Local Expert
                  </div>
                </div>
                
                {/* Host Info */}
                <div className="space-y-2">
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-black text-slate-900 tracking-tight">Dragos Moscu</h3>
                  <p className="text-brand-ocean font-bold text-sm sm:text-base md:text-lg">Founder, SUNMARBELLA</p>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed max-w-sm mx-auto">Based in Marbella since 2013. I inspect every home we list and stay close to every booking.</p>
                </div>
              </div>
              
              {/* Stats Row - Improved mobile spacing */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4 md:gap-6">
                <div className="text-center px-1 sm:px-4 md:px-6 py-2.5 sm:py-4 rounded-xl sm:rounded-2xl bg-slate-50 border border-slate-200 min-w-0 overflow-hidden">
                  <div className="text-base sm:text-xl md:text-2xl font-black text-brand-ocean leading-none">50+</div>
                  <div className="text-sm text-slate-500 font-semibold mt-1 leading-tight">Rentals Inspected</div>
                </div>
                <div className="text-center px-1 sm:px-4 md:px-6 py-2.5 sm:py-4 rounded-xl sm:rounded-2xl bg-slate-50 border border-slate-200 min-w-0 overflow-hidden">
                  <div className="text-base sm:text-xl md:text-2xl font-black text-brand-ocean leading-none">2hr</div>
                  <div className="text-sm text-slate-500 font-semibold mt-1 leading-tight">Response Time</div>
                </div>
                <div className="text-center px-1 sm:px-4 md:px-6 py-2.5 sm:py-4 rounded-xl sm:rounded-2xl bg-slate-50 border border-slate-200 min-w-0 overflow-hidden">
                  <div className="text-base sm:text-xl md:text-2xl font-black text-brand-ocean leading-none">2013</div>
                  <div className="text-sm text-slate-500 font-semibold mt-1 leading-tight">Local Since</div>
                </div>
              </div>
              
              {/* Host Testimonials Carousel */}
              <div className="relative">
                {/* Mobile: Swipeable track */}
                <div
                  ref={hostTestimonialsRef}
                  className="md:hidden ui-mobile-review-track flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2"
                >
                  {hostTestimonials.map((testimonial, idx) => (
                    <div 
                      key={idx} 
                      data-host-testimonial-card 
                      className="w-full shrink-0 snap-start bg-gradient-to-br from-brand-ocean/5 to-transparent rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-brand-ocean/10 shadow-sm"
                    >
                      <div className="space-y-3">
                        {/* Season Badge */}
                        <span className="inline-block px-3 py-1 bg-slate-100 text-slate-600 text-sm font-bold uppercase tracking-wider rounded-full">
                          {testimonial.season}
                        </span>
                        
                        {/* Rating with Score */}
                        <div className="flex items-center gap-2">
                          <div className="flex gap-0.5 text-brand-sun">
                            {[1, 2, 3, 4, 5].map((i) => (
                              <span key={i}>
                                <ProIcon icon="star" family="solid" className="w-4 h-4" />
                              </span>
                            ))}
                          </div>
                          <span className="text-slate-700 font-bold text-sm">{testimonial.rating.toFixed(1)}</span>
                        </div>
                        
                        <p className="text-slate-700 italic text-sm sm:text-base leading-relaxed">"{testimonial.quote}"</p>
                        
                        <div className="space-y-1">
                          <p className="text-slate-600 text-sm font-semibold">— {testimonial.byline}</p>
                          <div className="flex items-center gap-1.5 text-slate-500 text-sm">
                            <span className="text-slate-400"><ProIcon icon="bullseye-arrow" family="light" className="w-4 h-4" /></span>
                            <span>{testimonial.source}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop: Single card with nav buttons - Clickable & Swipeable */}
                <div className="hidden md:block">
                  <div 
                    className="bg-gradient-to-br from-brand-ocean/5 to-transparent rounded-3xl p-5 sm:p-8 border border-brand-ocean/10 shadow-sm cursor-pointer group relative overflow-hidden touch-pan-y"
                    onClick={handleCardClick}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  >
                    <div className="space-y-4">
                      {/* Header with Season Badge and Source */}
                      <div className="flex items-center justify-between">
                        <span className="inline-block px-3 py-1 bg-slate-100 text-slate-600 text-sm font-bold uppercase tracking-wider rounded-full">
                          {hostTestimonials[currentTestimonial].season}
                        </span>
                        <div className="flex items-center gap-1.5 text-slate-500 text-sm">
                          <span className="text-slate-400"><ProIcon icon="bullseye-arrow" family="light" className="w-4 h-4" /></span>
                          <span>{hostTestimonials[currentTestimonial].source}</span>
                        </div>
                      </div>
                      
                      {/* Rating */}
                      <div className="flex items-center gap-2">
                        <div className="flex gap-0.5 text-brand-sun">
                          {[1, 2, 3, 4, 5].map((i) => (
                            <span key={i}>
                              <ProIcon icon="star" family="solid" className="w-4 h-4" />
                            </span>
                          ))}
                        </div>
                        <span className="text-slate-700 font-bold">{hostTestimonials[currentTestimonial].rating.toFixed(1)}</span>
                      </div>
                      
                      <p className="text-slate-700 italic text-base sm:text-lg leading-relaxed">
                        "{hostTestimonials[currentTestimonial].quote}"
                      </p>
                      <p className="text-slate-600 font-semibold">— {hostTestimonials[currentTestimonial].byline}</p>
                    </div>
                    
                    {/* Navigation dots for desktop - integrated inside card */}
                    <div className="mt-6 flex items-center justify-center gap-2">
                      {hostTestimonials.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentTestimonial(idx);
                          }}
                          className={`w-2 h-2 rounded-full transition-colors ${idx === currentTestimonial ? 'bg-brand-ocean' : 'bg-brand-ocean/30 hover:bg-brand-ocean/50'}`}
                          aria-label={`Go to testimonial ${idx + 1}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Mobile: swipe is enough, no nav buttons needed */}
              </div>
            </div>
            
            {/* Right Column - Value Proposition */}
            <div className="space-y-3 sm:space-y-5 md:space-y-6 min-w-0">
              <p className="text-sm sm:text-sm font-bold uppercase tracking-widest text-brand-ocean">The SUNMARBELLA Promise</p>
              <h2 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-black text-brand-ocean tracking-tighter leading-tight">
                Your Family Holiday. Done Right.
              </h2>
              
              <div className="space-y-3 sm:space-y-4">
                <div className="flex gap-3 sm:gap-4">
                  <div className="flex-shrink-0 w-9 h-9 sm:w-12 sm:h-12 bg-brand-ocean/10 rounded-xl flex items-center justify-center">
                    <ProIcon icon="check" family="solid" className="w-4 h-4 sm:w-6 sm:h-6 text-brand-ocean" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm sm:text-base md:text-lg font-bold text-slate-900 mb-0.5 sm:mb-1">Every Rental Personally Inspected</h3>
                    <p className="text-sm sm:text-base text-slate-600">We check the details that decide whether a stay works or fails.</p>
                  </div>
                </div>
                
                <div className="flex gap-3 sm:gap-4">
                  <div className="flex-shrink-0 w-9 h-9 sm:w-12 sm:h-12 bg-brand-ocean/10 rounded-xl flex items-center justify-center">
                    <ProIcon icon="check" family="solid" className="w-4 h-4 sm:w-6 sm:h-6 text-brand-ocean" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm sm:text-base md:text-lg font-bold text-slate-900 mb-0.5 sm:mb-1">Local Team, Fast Response</h3>
                    <p className="text-sm sm:text-base text-slate-600">Real people in Marbella who know the home and act fast when needed.</p>
                  </div>
                </div>
                
                <div className="flex gap-3 sm:gap-4">
                  <div className="flex-shrink-0 w-9 h-9 sm:w-12 sm:h-12 bg-brand-ocean/10 rounded-xl flex items-center justify-center">
                    <ProIcon icon="check" family="solid" className="w-4 h-4 sm:w-6 sm:h-6 text-brand-ocean" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm sm:text-base md:text-lg font-bold text-slate-900 mb-0.5 sm:mb-1">Direct Booking, Real Support</h3>
                    <p className="text-sm sm:text-base text-slate-600">Better rates up to 20% cheaper than OTAs, clearer answers, no platform runaround.</p>
                  </div>
                </div>
              </div>
              
              {/* CTA Button - PRIMARY: Main conversion after value prop */}
              <div className="pt-2 sm:pt-4">
                <button
                  onClick={() => {
                    trackEvent('select_promotion', {
                      promotion_name: 'homepage_promise_section',
                      creative_name: 'browse_rentals_cta',
                    });
                    navigate('/destinations');
                  }}
                  className="ui-btn-primary w-full sm:w-auto px-6 sm:px-10 py-3.5 sm:py-5 min-h-[44px] sm:min-h-[52px] text-sm sm:text-base group"
                >
                  <span className="hidden sm:inline md:hidden">Browse Rentals</span>
                  <span className="hidden md:inline">Browse Our Vetted Rentals</span>
                  <span className="sm:hidden">Browse</span>
                  <ProIcon 
                    icon="chevron-right" 
                    family="light"
                    className="w-3.5 h-3.5 sm:w-4 sm:h-4 ml-2 transition-transform duration-200 group-hover:translate-x-1" 
                  />
                </button>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed mt-2 sm:mt-3">
                  Or <a href="/contact" className="text-brand-ocean font-bold hover:underline">tell us your dates</a> and we will shortlist the right homes
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Featured Properties */}
      <section className="py-10 sm:py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-start mb-6 sm:mb-10 md:mb-16 gap-3 sm:gap-6">
            <div className="space-y-2 sm:space-y-4 w-full md:w-auto">
              <p className="text-sm sm:text-sm font-bold uppercase tracking-widest text-brand-ocean">Featured Marbella Stays</p>
              <h2 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-black text-brand-ocean tracking-tighter leading-tight uppercase">Homes Families Book First</h2>
              <p className="text-slate-500 font-medium text-sm sm:text-base md:text-lg">The homes guests try to book first.</p>
              
              {/* Mobile: Button below subtitle - SECONDARY navigation */}
              <button
                onClick={() => {
                  trackEvent('select_promotion', {
                    promotion_name: 'homepage_featured_rentals',
                    creative_name: 'browse_all_rentals_cta_mobile',
                  });
                  navigate('/destinations');
                }}
                className="md:hidden ui-btn-secondary w-full px-6 py-3.5 min-h-[44px] sm:min-h-[52px] text-sm mt-2 group"
              >
                <span>Browse All Rentals</span>
                <ProIcon 
                  icon="chevron-right" 
                  family="light"
                  className="w-4 h-4 ml-2 transition-transform duration-200 group-hover:translate-x-1" 
                />
              </button>
            </div>
            
            {/* Desktop: Button aligned right - SECONDARY navigation */}
            <button
              onClick={() => {
                trackEvent('select_promotion', {
                  promotion_name: 'homepage_featured_rentals',
                  creative_name: 'browse_all_rentals_cta',
                });
                navigate('/destinations');
              }}
              className="hidden md:inline-flex ui-btn-secondary px-8 sm:px-10 py-4 sm:py-5 min-h-[52px] self-center whitespace-nowrap flex-shrink-0 group"
            >
              <span>Browse All Rentals</span>
              <ProIcon 
                icon="chevron-right" 
                family="light"
                className="w-4 h-4 ml-2 transition-transform duration-200 group-hover:translate-x-1" 
              />
            </button>
          </div>

          {/* 6-Property Grid Layout */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {featuredProperties.slice(0, 6).map((prop: any, index: number) => (
              <PropertyCard key={prop.id} property={prop} />
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-10 sm:py-16 md:py-24 bg-brand-ocean text-white overflow-hidden relative">
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Header with Platform Icons */}
          <div className="space-y-3 sm:space-y-6 mb-6 sm:mb-10 md:mb-16 text-center">
            <div className="text-center text-sm sm:text-sm font-medium text-white/90 leading-relaxed normal-case">What Families Say</div>
            <h3 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter text-brand-sand">4.9 / 5 Across 500+ Stays</h3>
            
            {/* Platform Badges - Desktop Only */}
            <div className="hidden md:flex items-center justify-center gap-6 mt-6">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full border border-white/20">
                <ProIcon icon="google" family="brands" className="w-5 h-5" />
                <span className="text-sm font-semibold">Google Reviews</span>
                <span className="text-brand-sun font-bold">4.9</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full border border-white/20">
                <ProIcon icon="star" family="solid" className="w-5 h-5 text-brand-sun" />
                <span className="text-sm font-semibold">Trustpilot</span>
                <span className="text-brand-sun font-bold">Excellent</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full border border-white/20">
                <ProIcon icon="bullseye-arrow" family="light" className="w-5 h-5" />
                <span className="text-sm font-semibold">Direct Bookings</span>
                <span className="text-brand-sun font-bold">500+</span>
              </div>
            </div>
          </div>

          {/* Mobile Reviews Carousel */}
          <div className="md:hidden">
            <div
              ref={mobileReviewsRef}
              className="ui-mobile-review-track flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2"
            >
              {reviews.map((review, idx) => (
                <div key={idx} data-review-card className="w-full shrink-0 snap-start bg-white/5 border border-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-5 space-y-3 sm:space-y-4">
                  {/* Season Badge */}
                  <span className="inline-block px-3 py-1 bg-slate-100 text-slate-600 text-sm font-bold uppercase tracking-wider rounded-full">
                    {review.season}
                  </span>
                  
                  {/* Rating with Score */}
                  <div className="flex items-center gap-2">
                    <div className="flex gap-0.5 text-brand-sun">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <span key={i}>
                          <ProIcon icon="star" family="solid" className="w-4 h-4" />
                        </span>
                      ))}
                    </div>
                    <span className="text-brand-sand font-bold text-sm">{review.rating.toFixed(1)}</span>
                  </div>
                  
                  <p className="text-brand-sand text-sm sm:text-base leading-relaxed">"{review.quote}"</p>
                  
                  <div className="space-y-1">
                    <p className="text-white text-sm sm:text-base font-semibold break-words">{review.byline}</p>
                    <div className="flex items-center gap-1.5 text-white/60 text-sm">
                      <span className="text-brand-sun/80">{getSourceIcon(review.source)}</span>
                      <span>{review.source}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Mobile Review Navigation */}
            <div className="flex items-center justify-center gap-4 mt-4">
              <button
                onClick={() => slideReviews('left')}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                aria-label="Previous review"
              >
                <ProIcon icon="chevron-left" family="light" className="w-5 h-5 text-white" />
              </button>
              <span className="text-sm text-white/70">
                Swipe or tap to navigate
              </span>
              <button
                onClick={() => slideReviews('right')}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                aria-label="Next review"
              >
                <ProIcon icon="chevron-right" family="light" className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Desktop Reviews Grid - Show All Reviews */}
          <div className="hidden md:block">
            {/* First 6 reviews - always visible */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 mb-6">
              {reviews.slice(0, 6).map((review, idx) => (
                <div key={idx} className="bg-white/5 border border-white/10 rounded-3xl p-5 sm:p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    {/* Season Badge */}
                    <span className="inline-block px-3 py-1 bg-slate-100 text-slate-600 text-sm font-bold uppercase tracking-wider rounded-full">
                      {review.season}
                    </span>
                    {/* Source Icon */}
                    <div className="flex items-center gap-1.5 text-white/60 text-sm">
                      <span className="text-brand-sun/80">{getSourceIcon(review.source)}</span>
                      <span>{review.source}</span>
                    </div>
                  </div>
                  
                  {/* Rating */}
                  <div className="flex items-center gap-2">
                    <div className="flex gap-0.5 text-brand-sun">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <span key={i}>
                          <ProIcon icon="star" family="solid" className="w-4 h-4" />
                        </span>
                      ))}
                    </div>
                    <span className="text-brand-sand font-bold">{review.rating.toFixed(1)}</span>
                  </div>
                  
                  <p className="text-brand-sand text-sm leading-relaxed">"{review.quote}"</p>
                  
                  <p className="text-white ui-button-text font-semibold break-words">{review.byline}</p>
                </div>
              ))}
            </div>
            
            {/* Remaining reviews - collapsible (single grid for proper flow) */}
            <div className={`transition-all duration-500 ${showAllReviews ? 'opacity-100 max-h-[2000px]' : 'opacity-0 max-h-0 overflow-hidden'}`}>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
                {reviews.slice(6).map((review, idx) => (
                  <div key={idx} className="bg-white/5 border border-white/10 rounded-3xl p-5 sm:p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      {/* Season Badge */}
                      <span className="inline-block px-3 py-1 bg-slate-100 text-slate-600 text-sm font-bold uppercase tracking-wider rounded-full">
                        {review.season}
                      </span>
                      {/* Source Icon */}
                      <div className="flex items-center gap-1.5 text-white/60 text-sm">
                        <span className="text-brand-sun/80">{getSourceIcon(review.source)}</span>
                        <span>{review.source}</span>
                      </div>
                    </div>
                    
                    {/* Rating */}
                    <div className="flex items-center gap-2">
                      <div className="flex gap-0.5 text-brand-sun">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <span key={i}>
                            <ProIcon icon="star" family="solid" className="w-4 h-4" />
                          </span>
                        ))}
                      </div>
                      <span className="text-brand-sand font-bold">{review.rating.toFixed(1)}</span>
                    </div>
                    
                    <p className="text-brand-sand text-sm leading-relaxed">"{review.quote}"</p>
                    
                    <p className="text-white ui-button-text font-semibold break-words">{review.byline}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Show More/Less Button - Desktop Only */}
            <div className="mt-8 text-center">
              <button
                onClick={() => setShowAllReviews(!showAllReviews)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full text-brand-sand font-semibold transition-colors"
              >
                <span>{showAllReviews ? 'Show Fewer Reviews' : `Show All ${reviews.length} Reviews`}</span>
                <ProIcon icon={showAllReviews ? 'chevron-right' : 'chevron-right'} family="light" className={`transition-transform ${showAllReviews ? 'rotate-90' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Soft-conversion block for guests who browse but are not ready to enquire yet */}
      <section className="bg-white py-10 sm:py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <NewsletterSignup
            variant="feature"
            source="home_bottom"
            eyebrow="Not Ready To Book Yet?"
            title="Get Last-Minute Marbella Rental Deals"
            description="Stay close to the best openings without checking every day. We will send newly opened dates, price drops, and direct-booking offers for Marbella stays worth acting on."
            buttonLabel="Get Deals"
            tags={['home', 'last-minute-deals']}
          />
        </div>
      </section>
    </main>
  );
};
